import React, { FC, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { Spinner, RadioOption } from '@td-client-frontend/common';
import { useMarketingProfileAvailableOwners } from '../../../hooks/useMarketingProfile';

export interface OrgByIdProps {
  id: string;
  name: string;
  ancestors?: OrgByIdProps[];
}

interface OrgOwnershipProps {
  id: string;
  owner: OrgByIdProps | null;
  onChange(ownershipName: OrgByIdProps): void;
}

export const OrgOwnership: FC<OrgOwnershipProps> = ({ id, owner, onChange }) => {
  const { data: organizationById, isLoading } = useMarketingProfileAvailableOwners(id);

  const orgAndAncestors = organizationById ? [organizationById, ...(organizationById.ancestors || [])] : [];

  const changeHandler = useCallback(
    ({ id, name }: OrgByIdProps) => {
      onChange({ id, name });
    },
    [onChange]
  );

  useEffect(() => {
    if (!isLoading && !owner) {
      const { id, name } = orgAndAncestors[orgAndAncestors.length - 1];
      changeHandler({ id, name });
    }
  }, [changeHandler, isLoading, orgAndAncestors, owner]);

  return (
    <>
      <p className="text-lg mb-4 text-grey-45">Please choose the organization this profile will be visible under</p>
      <div className="max-w-full text-lg overflow-auto h-44 text-gray-60">
        {!isLoading ? (
          orgAndAncestors.reverse().map((item, index) => {
            const checked = item.id === owner?.id;
            return (
              <div key={item.name} className={`ml-${(index + 1) * 4} mb-4`}>
                <RadioOption
                  name={`orgLevel-${index}`}
                  label={item.name}
                  value={item.name}
                  input={{
                    'type': 'radio',
                    'checked': checked,
                    'data-testid': item.name
                  }}
                  onChange={() => changeHandler(item)}
                  labelClassName="text-lg gap-1 leading-5 text-gray-60 focus-within:none"
                  checkboxClassName={clsx(checked && 'border-blue-80')}
                  activeCheckBoxClassName="bg-blue-80"
                />
              </div>
            );
          })
        ) : (
          <>
            <div className="flex mt-4">
              <span className="mr-2">
                <Spinner />
              </span>
              Finding organizations...
            </div>
          </>
        )}
      </div>
    </>
  );
};
